import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  type Theme,
  Tooltip,
} from '@mui/material';
import { type ReactNode, useId } from 'react';
import { Button, type ButtonProps } from '../../primitives/button';
import { Chip, type ChipProps } from '../../primitives/chip/chip';
import { Typography } from '../../primitives/typography/typography';

export type SettingsCardStatus = { label: string; color: ChipProps['color'] };

const fieldMarginOf = (theme: Theme) => theme.staticVars?.fieldMargin || '0px';

export function SettingsCard(props: {
  title: string;
  subheader: string;
  status?: SettingsCardStatus;
  children: ReactNode;
}) {
  const titleId = useId();

  return (
    <Card
      variant="outlined"
      component="section"
      aria-labelledby={titleId}
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <CardHeader
        title={props.title}
        slotProps={{ title: { id: titleId } }}
        action={
          props.status && (
            <Chip
              variant="muted"
              size="small"
              color={props.status.color}
              label={props.status.label}
            />
          )
        }
        sx={{ pb: 0.5 }}
      />
      <CardContent
        sx={{
          pt: 0,
          '&:last-child': { pb: 2 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography.Subtitle>{props.subheader}</Typography.Subtitle>
        {props.children}
      </CardContent>
    </Card>
  );
}

export function SettingsCardGrid(props: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
      }}
    >
      {props.children}
    </Box>
  );
}

export function SettingsCardSkeleton() {
  return (
    <Card variant="outlined">
      <CardContent>
        <Skeleton variant="rectangular" sx={{ width: '100%', height: '100px' }} />
      </CardContent>
    </Card>
  );
}

/** The card's bottom row: any field on the left, the action bottom-aligned with it on the right. */
export function SettingsCardActions(props: { children: ReactNode }) {
  return (
    <Stack direction="row" sx={{ mt: 'auto', gap: 2, alignItems: 'flex-end' }}>
      {props.children}
    </Stack>
  );
}

/** A card's action; while disabled, hovering it says what it is waiting for. */
export function SettingsCardButton({
  disabledReason,
  ...props
}: ButtonProps & { disabledReason?: string }) {
  return (
    <Tooltip title={props.disabled && disabledReason ? disabledReason : ''}>
      {/* The theme's field margin sits under every input, so the action takes it too to line up. */}
      <Box
        component="span"
        sx={(theme) => ({ display: 'inline-flex', ml: 'auto', mb: fieldMarginOf(theme) })}
      >
        <Button size="small" {...props} sx={(theme) => ({ minWidth: theme.spacing(15) })} />
      </Box>
    </Tooltip>
  );
}
